import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import CtaBand from "./UI/CtaBand";
import Icon from "./UI/Icon";
import trustOne from "./../assets/trust-0.jpg";
import trustTwo from "./../assets/trust-1.jpg";
import trustThree from "./../assets/trust-2.jpg";

const values = [
  {
    icon: "compass",
    title: "Design-first approach",
    text: "We start with measurements, sketches and finishes, making sure the piece fits your space and your routine before we pick up a saw.",
  },
  {
    icon: "kitchen",
    title: "Homes & small businesses",
    text: "From residential storage and kitchens to smart retail shelving, we build with the same attention to detail and minimal disruption.",
  },
  {
    icon: "clock",
    title: "Repairs & aftercare",
    text: "We can refresh worn timber, adjust sticking doors and keep your joinery looking sharp long after installation.",
  },
];

const stats = [
  { n: "10+", label: "Years’ experience" },
  { n: "100%", label: "Insured & guaranteed" },
  { n: "5★", label: "Local reputation" },
];

export default function AboutUs() {
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "About Us" },
  ];

  return (
    <>
      <Seo
        title="About Cambridge Carpenter | Local Carpenter & Joiner in Cambridge"
        description="Cambridge Carpenter is a local carpentry and joinery team creating thoughtful, hard-wearing timberwork for homes and gardens across Cambridgeshire."
        canonical="https://www.cambridgecarpenter.co.uk/about-us/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="Based in Cambridge"
        title="Your local carpentry specialist"
        intro="A Cambridge carpentry and joinery team crafting fitted furniture, decking and bespoke timber builds across Cambridgeshire."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div className="relative mb-12 lg:mb-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden border border-hairline">
                <img src={trustOne} alt="Cambridge Carpenter project" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="space-y-4">
                <div className="overflow-hidden border border-hairline">
                  <img src={trustTwo} alt="Cambridge Carpenter project detail" className="w-full h-40 object-cover" loading="lazy" />
                </div>
                <div className="overflow-hidden border border-hairline">
                  <img src={trustThree} alt="Joinery work by Cambridge Carpenter" className="w-full h-40 object-cover" loading="lazy" />
                </div>
              </div>
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-oak text-paper px-6 py-4 shadow-lg text-center">
              <h2 className="font-display text-xl font-semibold">Experts you can trust</h2>
            </div>
          </div>

          <div className="prose-links">
            <SectionHeading eyebrow="Who we are" title="Carpentry built on care and craft" />
            <div className="text-bark text-lg leading-8 mt-6 space-y-5">
              <p>
                Cambridge Carpenter is a local carpentry and joinery team crafting fitted furniture,
                kitchens, decking and bespoke timber builds across Cambridge and the surrounding
                villages. We’re small enough to care about every detail and experienced enough to get
                it right first time.
              </p>
              <p>
                We believe good carpentry is quiet: doors that close sweetly, joints that stay tight
                and storage that just works. Whether it’s a single alcove unit or a full room refit,
                we bring the same attention to design, materials and finish.
              </p>
              <p>
                Explore our <Link to="/carpentry-services">carpentry services</Link>, view{" "}
                <Link to="/our-work">recent projects</Link>, or{" "}
                <Link to="/contact">start your project</Link> with a quick message.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-9">
              {stats.map((s) => (
                <div key={s.label} className="border border-hairline bg-linen p-4 text-center">
                  <span className="block font-display text-2xl sm:text-3xl font-semibold text-oak">{s.n}</span>
                  <span className="text-bark text-xs sm:text-sm mt-1 block">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-linen border-y border-hairline">
        <div className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
          <SectionHeading eyebrow="How we work" title="What you can expect" center />
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {values.map((v) => (
              <div key={v.title} className="bg-paper border border-hairline p-6">
                <div className="w-11 h-11 flex items-center justify-center bg-oak/10 text-oak mb-4">
                  <Icon name={v.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-xl text-ink mb-2">{v.title}</h3>
                <p className="text-bark leading-7">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
